import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

// Clear gift selections when cart becomes empty (prevents stale pre-selections)
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.itemCount === 0) {
    fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _selected_gifts: '' } }),
      keepalive: true,
    });
  }
});

class CartGifts extends Component {
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.maxGifts = parseInt(this.dataset.maxGifts, 10) || 0;
    this.hiddenInput = this.querySelector('[data-gifts-hidden]');
    this.addEventListener('change', this.#handleChange);
    this.#reconcileGifts();
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * After any cart update, morph re-renders checkboxes from server HTML which
   * resets JS-only disabled states. Defer reconciliation until morph completes.
   */
  #handleCartUpdate = () => {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.maxGifts = parseInt(this.dataset.maxGifts, 10) || 0;
      this.hiddenInput = this.querySelector('[data-gifts-hidden]');
      this.#reconcileGifts();
    });
  };

  /**
   * Trims excess checked gifts when maxGifts decreases, then enforces the limit.
   * Called on connect and after every cart update morph.
   */
  #reconcileGifts() {
    const checked = Array.from(
      this.querySelectorAll('input[type="checkbox"]:checked')
    );

    if (checked.length > this.maxGifts) {
      // Uncheck excess gifts (keep first N selected)
      checked.slice(this.maxGifts).forEach((cb) => {
        cb.checked = false;
      });
      this.#syncHiddenInput();
      this.#saveToCart();
    }

    this.#enforceLimit();
  }

  #handlePageShow = (event) => {
    // Only sync from cart on bfcache restore (back/forward navigation).
    // On normal page load, Liquid already renders the correct checkbox state.
    // Without this check, the async fetch can race with user interactions
    // and reset gift selections before they're saved.
    if (event.persisted) {
      this.#syncFromCart();
    }
  };

  async #syncFromCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const raw = cart.attributes?._selected_gifts || '';
      const selectedArray = raw.split(', ').filter(Boolean);

      this.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = selectedArray.includes(cb.value);
      });

      this.#enforceLimit();
      this.#syncHiddenInput();
    } catch (error) {
      // Ignore fetch errors on page restore
    }
  }

  #handleChange = () => {
    // Synchronous: enforce limit and sync hidden field immediately
    this.#enforceLimit();
    this.#syncHiddenInput();
    // Save to cart immediately (no debounce — gift selections are infrequent
    // and must persist before the user navigates to checkout)
    this.#saveToCart();
  };

  #syncHiddenInput() {
    if (!this.hiddenInput) return;
    const selected = Array.from(
      this.querySelectorAll('input[type="checkbox"]:checked')
    ).map((cb) => cb.value);
    this.hiddenInput.value = selected.join(', ');
  }

  #enforceLimit() {
    const checkboxes = this.querySelectorAll('input[type="checkbox"]');
    const checked = this.querySelectorAll('input[type="checkbox"]:checked');

    checkboxes.forEach((cb) => {
      const item = cb.closest('.cart-gifts__item');
      if (!cb.checked && checked.length >= this.maxGifts) {
        cb.disabled = true;
        item?.classList.add('cart-gifts__item--disabled');
      } else {
        cb.disabled = false;
        item?.classList.remove('cart-gifts__item--disabled');
      }
    });
  }

  async #saveToCart() {
    const selected = Array.from(
      this.querySelectorAll('input[type="checkbox"]:checked')
    ).map((cb) => cb.value);

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            _selected_gifts: selected.join(', '),
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
        keepalive: true,
      });
    } catch (error) {
      // Ignore abort errors
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('cart-gifts-component')) {
  customElements.define('cart-gifts-component', CartGifts);
}
